<div class="flex flex-col gap-6">
@if (salesOrder; as order) {
  @if (invoiceSums$ | async; as sums) {
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
      <app-card-shell padding="md">
        <p
          class="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1"
        >
          {{ 'COMPONENTS.SALES_ORDERS.FINANCE.SUMS.NET' | translate }}
        </p>
        <p class="text-lg font-bold text-neutral-900 dark:text-neutral-50 tabular-nums">
          {{ sums.invoicedExcludingTax | number: '1.2-2' }}
        </p>
      </app-card-shell>
      <app-card-shell padding="md">
        <p
          class="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1"
        >
          {{ 'COMPONENTS.SALES_ORDERS.FINANCE.SUMS.VAT' | translate }}
        </p>
        <p class="text-lg font-bold text-neutral-700 dark:text-neutral-300 tabular-nums">
          {{ sums.invoicedTax | number: '1.2-2' }}
        </p>
      </app-card-shell>
      <app-card-shell padding="md" class="finance-tab__sum-card--accent">
        <p
          class="text-xs font-semibold uppercase tracking-wider text-blue-500 dark:text-blue-400 mb-1"
        >
          {{ 'COMPONENTS.SALES_ORDERS.FINANCE.SUMS.TOTAL_INVOICED' | translate }}
        </p>
        <p class="text-lg font-bold text-blue-700 dark:text-blue-300 tabular-nums">
          {{ sums.invoicedIncludingTax | number: '1.2-2' }}
        </p>
      </app-card-shell>
      <app-card-shell padding="md">
        <p
          class="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500 mb-1"
        >
          {{ 'COMPONENTS.SALES_ORDERS.FINANCE.SUMS.TOTAL_PAID' | translate }}
        </p>
        <p class="text-lg font-bold text-green-700 dark:text-green-400 tabular-nums">
          {{ sums.paidIncludingTax | number: '1.2-2' }}
        </p>
      </app-card-shell>
    </div>
  }
}

@if (paymentStatus$ | async; as payment) {
  <app-card-shell padding="lg" class="sales-order-finance-tab__payment-status">
    <h2
      class="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider m-0 mb-4"
    >
      {{ 'COMPONENTS.SALES_ORDERS.FINANCE.PAYMENT_STATUS.TITLE' | translate }}
    </h2>
    <div class="flex items-center gap-3">
      <div
        class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        [class]="payment.iconBgClass"
        aria-hidden="true"
      >
        <svg
          class="w-5 h-5"
          [class]="payment.iconTextClass"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          @if (payment.iconVariant === 'check') {
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 13l4 4L19 7"
            />
          } @else {
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          }
        </svg>
      </div>
      <div>
        <p class="text-sm font-semibold text-neutral-900 dark:text-neutral-50 m-0">
          {{ payment.labelKey | translate }}
        </p>
        <p class="text-xs text-neutral-500 dark:text-neutral-400 m-0 mt-0.5">
          {{
            'COMPONENTS.SALES_ORDERS.FINANCE.PAYMENT_STATUS.PAID_OF_TOTAL'
              | translate
                : {
                    paid: (payment.paidAmount | number: '1.2-2'),
                    total: (payment.totalAmount | number: '1.2-2')
                  }
          }}
        </p>
      </div>
    </div>
  </app-card-shell>
}

<app-card-shell padding="lg">
  <div class="flex flex-col gap-5">
    <div class="flex items-center justify-between gap-3">
      <h2
        class="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider m-0"
      >
        {{ 'COMPONENTS.SALES_ORDERS.FINANCE.INVOICES_TITLE' | translate }}
      </h2>

      @if (canCreateInvoice && !disabled) {
        <button
          type="button"
          class="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          (click)="requestCreateInvoice()"
        >
          + {{ 'COMPONENTS.SALES_ORDERS.INVOICES.NEW_BUTTON' | translate }}
        </button>
      }
    </div>

    @if (isLoading) {
      <div
        class="text-center py-12 text-sm text-neutral-500 dark:text-neutral-400"
        role="status"
        aria-live="polite"
      >
        {{ 'MESSAGES.LOADING' | translate }}
      </div>
    } @else if (hasLoadError) {
      <div
        class="sales-order-finance-tab__error flex flex-col items-center gap-3 px-4 py-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
        role="alert"
      >
        <p class="m-0 text-sm">
          {{ 'COMPONENTS.SALES_ORDERS.ERRORS.LOAD_FAILED' | translate }}
        </p>
        <button
          type="button"
          class="sales-order-finance-tab__retry-btn px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          (click)="retry()"
        >
          {{ 'BUTTONS.RETRY' | translate }}
        </button>
      </div>
    } @else if ((invoiceRows$ | async)?.length) {
      <div class="flex flex-col gap-2">
        @for (invoice of invoiceRows$ | async; track invoice.id) {
          <div
            class="flex items-center gap-4 p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700 focus-visible:border-neutral-200 dark:focus-visible:border-neutral-700 focus-visible:outline-none transition-colors cursor-pointer"
            tabindex="0"
            role="button"
            (click)="openInvoice(invoice.id)"
            (keydown.enter)="openInvoice(invoice.id)"
            (keydown.space)="openInvoice(invoice.id)"
          >
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              [class]="invoice.iconBgClass"
              aria-hidden="true"
            >
              <svg
                class="w-5 h-5"
                [class]="invoice.iconTextClass"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50 m-0 truncate">
                {{ invoice.number }}
              </p>
              <p class="text-xs text-neutral-400 dark:text-neutral-500 m-0 mt-0.5">
                {{ invoice.invoiceDate ? (invoice.invoiceDate | date: 'yyyy-MM-dd') : '-' }}
              </p>
            </div>
            <div class="flex flex-col items-end gap-1.5 shrink-0">
              <p class="text-sm font-semibold text-neutral-900 dark:text-neutral-50 m-0 tabular-nums">
                {{ invoice.total | number: '1.2-2' }}
              </p>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded-full"
                [class]="invoice.statusBadgeClass"
              >
                {{ invoice.statusLabelKey | translate }}
              </span>
              @if (invoice.canMarkPaid && !disabled) {
                <button
                  type="button"
                  class="px-3 py-1 text-xs font-medium rounded-md border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 disabled:opacity-60 disabled:cursor-progress transition-colors"
                  [disabled]="invoice.isMutating"
                  (click)="markPaid(invoice); $event.stopPropagation()"
                >
                  @if (invoice.isMutating) {
                    {{ 'COMPONENTS.SALES_ORDERS.FINANCE.MARK_PAID_LOADING' | translate }}
                  } @else {
                    {{ 'COMPONENTS.SALES_ORDERS.FINANCE.MARK_PAID' | translate }}
                  }
                </button>
              }
              @if (invoice.errorKey; as errorKey) {
                <p
                  class="text-xs text-red-600 dark:text-red-400 m-0 text-right"
                  role="alert"
                >
                  {{ errorKey | translate }}
                </p>
              }
            </div>
          </div>
        }
      </div>
    } @else {
      <div class="text-center py-12 text-sm text-neutral-400 dark:text-neutral-500">
        {{ 'COMPONENTS.SALES_ORDERS.FINANCE.SUMS.EMPTY' | translate }}
      </div>
    }
  </div>
</app-card-shell>
</div>

<app-reusable-modal
  [isOpen]="isDetailModalOpen"
  [panelClass]="'max-w-5xl'"
  [contentScoped]="true"
  [title]="'COMPONENTS.INVOICES.DETAIL.HEADER' | translate"
  (closed)="closeDetailModal()"
>
  <app-invoice-modal
    [companyId]="companyId"
    [invoiceId]="selectedInvoiceId"
    [isOpen]="isDetailModalOpen"
    (closed)="closeDetailModal()"
    (paid)="onInvoicePaid()"
    (deleted)="onInvoiceDeleted()"
  />
</app-reusable-modal>
